import { useColorModeValue } from '@chakra-ui/react';

/**
 * Naming adapter for the application's existing single-root Chakra theme.
 *
 * No campaign palette or color-mode overrides live here. The root theme uses
 * cssVarPrefix: 'chakra'; semantic CSS variables therefore work in Chakra props,
 * plain SVG icons and CSS gradients, and follow preset/mode changes immediately.
 * Keep actual colors in theme/presets, never in this module.
 */
const color = (role) => `var(--chakra-colors-${role.replace(/\./g, '-')})`;
const shadow = (role) => `var(--chakra-shadows-${role})`;

const feedback = (role) => Object.freeze({
  bg: color(`${role}.subtle`),
  fg: color(role),
  border: color(role),
});

const infoTone = feedback('info');
const roles = Object.freeze({
  page: color('bg.base'),
  surface: color('bg.surface'),
  surfaceSubtle: color('bg.subtle'),
  surfaceElevated: color('bg.elevated'),
  inputBg: color('bg.input'),
  tableHeader: color('erp.table.header'),
  textPrimary: color('text.primary'),
  textSecondary: color('text.secondary'),
  textMuted: color('text.muted'),
  iconMuted: color('text.muted'),
  border: color('border.default'),
  borderSubtle: color('border.subtle'),
  borderStrong: color('border.strong'),
  accent: color('accent.solid'),
  accentInk: color('text.accent'),
  accentHover: color('accent.hover'),
  accentSubtle: color('accent.subtle'),
  textOnAccent: color('text.onAccent'),
  success: color('success'),
  danger: color('danger'),
  dangerSubtle: color('danger.subtle'),
  warning: color('warning'),
  info: color('info'),
  focusRing: color('focus.ring'),
  shadowCard: shadow('card'),
  tones: Object.freeze({
    neutral: Object.freeze({
      bg: color('bg.elevated'),
      fg: color('text.secondary'),
      border: color('border.default'),
    }),
    blue: feedback('secondary'),
    // Preserve the existing status key, not the old hardcoded purple palette.
    // Preparation uses the root info role and still has its own label/spinner.
    purple: infoTone,
    green: feedback('success'),
    amber: feedback('warning'),
    red: feedback('danger'),
  }),
});

// Colors are the SAME semantic roles in either mode. Only native date/time
// input color-scheme needs a JS mode value. Chakra owns the actual color switch.
export const LIGHT = Object.freeze({ ...roles, mode: 'light' });
export const DARK = Object.freeze({ ...roles, mode: 'dark' });
export function useCampaignUI() {
  return useColorModeValue(LIGHT, DARK);
}

// Inherit the exact preset gradient, text, padding, hover and disabled rules.
export const primaryButton = () => ({ variant: 'brand' });

// The root's default Button variant is brand, so colorScheme='red' alone would
// still inherit a brand gradient. Explicit neutral outline + feedback roles
// makes a destructive confirmation distinct without adding a new global theme.
export const dangerButton = (ui) => ({
  variant: 'neutralOutline',
  color: ui.danger,
  borderColor: ui.danger,
  _hover: {
    bg: ui.dangerSubtle,
    color: ui.danger,
    borderColor: ui.danger,
    _disabled: { bg: 'transparent', borderColor: ui.danger },
  },
  _active: { bg: ui.dangerSubtle, color: ui.danger },
});
